// ReleaseNotes — admin screen at /admin/release-notes (PLEX-4101).
//
// Lets a Plex admin draft, edit, publish, and retract the product release
// notes surfaced to every user by the v2 "What's New" dialog
// (src/pages/workspace/ReleaseNotesModal.js). Backed by rd's /api/admin/release-notes
// endpoints via ../../queries/releaseNotesApi.
//
// Highlight shapes: `generate` returns highlights as PLAIN STRINGS parsed from
// the source text. The rendered modal expects RICH OBJECTS ({icon,title,body,
// tryIt?,badge?}). The highlight editor lets the admin PROMOTE each string into
// that object shape before publishing.

import { useCallback, useEffect, useState } from "react";
import { inject, observer } from "mobx-react";

import {
  fetchAdminReleaseNotes,
  generateReleaseNote,
  createReleaseNote,
  updateReleaseNote,
  publishReleaseNote,
  retractReleaseNote,
} from "../../queries/releaseNotesApi";
import "./ReleaseNotes.css";

// ─── Shape helpers ───────────────────────────────────────────────────────────

// The rich highlight fields the v2 modal renders. Keep in sync with
// ReleaseNotesModal.js ({icon,title,body,tryIt?,badge?}).
export const RICH_HIGHLIGHT_FIELDS = ["icon", "title", "body", "tryIt", "badge"];

export const noteId = (note) => note?.id ?? note?.note_id ?? null;

const isRichHighlight = (h) => h != null && typeof h === "object";

// Promote a plain-string highlight into the rich object shape. The parsed
// bullet text is short, so it seeds `title`; the admin fills in the rest.
export const promoteHighlight = (h) =>
  isRichHighlight(h)
    ? h
    : { icon: "", title: String(h ?? ""), body: "", tryIt: "", badge: "" };

const emptyRichHighlight = () => ({ icon: "", title: "", body: "", tryIt: "", badge: "" });

// Tolerate the several envelope shapes rd may return.
const unwrapList = (resp) => {
  if (Array.isArray(resp)) return resp;
  return resp?.notes ?? resp?.content?.notes ?? resp?.content ?? [];
};
const unwrapNote = (resp) => resp?.note ?? resp?.content?.note ?? resp?.content ?? resp;

// ─── Component ───────────────────────────────────────────────────────────────

const ReleaseNotes = ({ userStore }) => {
  const authData = userStore?.authData;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  // Generate-draft form.
  const [genVersion, setGenVersion] = useState("");
  const [genTitle, setGenTitle] = useState("");
  const [genSubtitle, setGenSubtitle] = useState("");
  const [genSource, setGenSource] = useState("");
  const [generating, setGenerating] = useState(false);

  // The draft currently open in the editor (a working copy).
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!authData) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchAdminReleaseNotes(authData);
      setNotes(unwrapList(resp));
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [authData]);

  useEffect(() => { load(); }, [load]);

  const openEditor = (note) => {
    setDraft({
      ...note,
      version: note.version ?? "",
      title: note.title ?? "",
      subtitle: note.subtitle ?? "",
      highlights: Array.isArray(note.highlights) ? note.highlights : [],
    });
    setStatus(null);
  };

  const handleGenerate = async () => {
    if (!genVersion.trim() || !genSource.trim()) {
      setStatus("Version and source text are required to generate a draft.");
      return;
    }
    setGenerating(true);
    setStatus(null);
    try {
      const payload = { version: genVersion.trim(), source_text: genSource };
      if (genTitle.trim()) payload.title = genTitle.trim();
      if (genSubtitle.trim()) payload.subtitle = genSubtitle.trim();
      const resp = await generateReleaseNote(authData, payload);
      const note = unwrapNote(resp);
      setStatus(`Draft generated for ${note?.version ?? genVersion}.`);
      setGenSource("");
      await load();
      if (note) openEditor(note);
    } catch (e) {
      setStatus(`Generate failed: ${e?.message || e}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateBlank = async () => {
    if (!genVersion.trim()) {
      setStatus("A version is required to create a draft.");
      return;
    }
    setGenerating(true);
    setStatus(null);
    try {
      const resp = await createReleaseNote(authData, {
        version: genVersion.trim(),
        title: genTitle.trim(),
        subtitle: genSubtitle.trim(),
        highlights: [],
      });
      const note = unwrapNote(resp);
      setStatus(`Blank draft created for ${note?.version ?? genVersion}.`);
      await load();
      if (note) openEditor(note);
    } catch (e) {
      setStatus(`Create failed: ${e?.message || e}`);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Draft editing (local working copy) ──────────────────────────────────
  const setDraftField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const setHighlight = (i, next) =>
    setDraft((d) => {
      const highlights = [...d.highlights];
      highlights[i] = next;
      return { ...d, highlights };
    });

  const setHighlightField = (i, field, value) =>
    setHighlight(i, { ...promoteHighlight(draft.highlights[i]), [field]: value });

  const removeHighlight = (i) =>
    setDraft((d) => ({ ...d, highlights: d.highlights.filter((_, j) => j !== i) }));

  const addHighlight = () =>
    setDraft((d) => ({ ...d, highlights: [...d.highlights, emptyRichHighlight()] }));

  const handleSave = async () => {
    const id = noteId(draft);
    if (id == null) return;
    setSaving(true);
    setStatus(null);
    try {
      await updateReleaseNote(authData, id, {
        version: draft.version,
        title: draft.title,
        subtitle: draft.subtitle,
        highlights: draft.highlights,
      });
      setStatus(`Saved draft ${draft.version}.`);
      await load();
    } catch (e) {
      setStatus(`Save failed: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (note) => {
    const id = noteId(note);
    if (id == null) return;
    setStatus(null);
    try {
      await publishReleaseNote(authData, id);
      setStatus(`Published ${note.version}.`);
      await load();
    } catch (e) {
      setStatus(`Publish failed: ${e?.message || e}`);
    }
  };

  const handleRetract = async (note) => {
    const id = noteId(note);
    if (id == null) return;
    setStatus(null);
    try {
      await retractReleaseNote(authData, id);
      setStatus(`Retracted ${note.version}.`);
      await load();
    } catch (e) {
      setStatus(`Retract failed: ${e?.message || e}`);
    }
  };

  const editingId = noteId(draft);

  return (
    <div className="rn">
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow section-eyebrow">What&apos;s New</div>
          <h1 className="serif hero-display">Release Notes</h1>
          <p className="lede">
            Draft, edit, and publish the product release notes shown to every
            user in the &quot;What&apos;s New&quot; dialog. Generate a draft from
            raw notes, promote each highlight into its rich form, then publish.
          </p>
        </div>
        <div className="page-hero-actions">
          <button
            className="btn ghost button-ghost"
            data-testid="rn-refresh"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="err" data-testid="rn-error">Error: {error}</div>}
      {status && <div className="rn-status" data-testid="rn-status">{status}</div>}

      {/* ─── Generate a draft ─────────────────────────────────────────── */}
      <section className="rn-card" data-testid="rn-generate">
        <h2 className="serif">Generate a draft</h2>
        <p className="rn-hint">
          Paste raw release notes as source text; the backend parses them into
          highlight bullets you can then refine below.
        </p>
        <div className="rn-form-grid">
          <label className="rn-field">
            <span className="rn-label">Version</span>
            <input
              type="text"
              className="rn-input"
              data-testid="rn-gen-version"
              placeholder="10.1.0"
              value={genVersion}
              onChange={(e) => setGenVersion(e.target.value)}
            />
          </label>
          <label className="rn-field">
            <span className="rn-label">Title (optional)</span>
            <input
              type="text"
              className="rn-input"
              data-testid="rn-gen-title"
              placeholder="What's new in Plex"
              value={genTitle}
              onChange={(e) => setGenTitle(e.target.value)}
            />
          </label>
          <label className="rn-field">
            <span className="rn-label">Subtitle (optional)</span>
            <input
              type="text"
              className="rn-input"
              data-testid="rn-gen-subtitle"
              placeholder="Highlights from this release"
              value={genSubtitle}
              onChange={(e) => setGenSubtitle(e.target.value)}
            />
          </label>
        </div>
        <label className="rn-field rn-field--full">
          <span className="rn-label">Source text</span>
          <textarea
            className="rn-textarea"
            data-testid="rn-gen-source"
            rows={6}
            placeholder="- Faster graph loading&#10;- New evidence-chain export&#10;- Coach memory improvements"
            value={genSource}
            onChange={(e) => setGenSource(e.target.value)}
          />
        </label>
        <div className="rn-actions">
          <button
            className="btn primary button-primary"
            data-testid="rn-generate-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "Working…" : "Generate draft"}
          </button>
          <button
            className="btn ghost button-ghost"
            data-testid="rn-create-blank-btn"
            onClick={handleCreateBlank}
            disabled={generating}
          >
            Create blank draft
          </button>
        </div>
      </section>

      {/* ─── Existing notes ───────────────────────────────────────────── */}
      <section className="rn-card">
        <h2 className="serif">All release notes</h2>
        {loading && <div className="empty" data-testid="rn-loading">Loading…</div>}
        {!loading && notes.length === 0 && (
          <div className="empty" data-testid="rn-empty">No release notes yet.</div>
        )}
        {!loading && notes.length > 0 && (
          <table className="rn-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Title</th>
                <th>Status</th>
                <th>Highlights</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => {
                const id = noteId(note);
                const published = note.status === "published";
                const isEditing = id != null && id === editingId;
                return (
                  <tr key={id ?? note.version} data-testid="rn-row">
                    <td>{note.version}</td>
                    <td>{note.title || <span className="rn-muted">—</span>}</td>
                    <td>
                      <span
                        className={`chip ${published ? "good" : "warn"}`}
                        data-testid="rn-status-chip"
                      >
                        {note.status || "draft"}
                      </span>
                    </td>
                    <td>{(note.highlights || []).length}</td>
                    <td className="rn-row-actions">
                      {!published && (
                        <button
                          className="btn small button-ghost"
                          data-testid="rn-edit-btn"
                          onClick={() => openEditor(note)}
                          disabled={isEditing}
                        >
                          {isEditing ? "Editing" : "Edit"}
                        </button>
                      )}
                      {!published && (
                        <button
                          className="btn small primary button-primary"
                          data-testid="rn-publish-btn"
                          onClick={() => handlePublish(note)}
                        >
                          Publish
                        </button>
                      )}
                      {published && (
                        <button
                          className="btn small button-ghost"
                          data-testid="rn-retract-btn"
                          onClick={() => handleRetract(note)}
                        >
                          Retract
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ─── Draft editor ─────────────────────────────────────────────── */}
      {draft && (
        <section className="rn-card" data-testid="rn-editor">
          <div className="rn-editor-head">
            <h2 className="serif">Edit draft {draft.version}</h2>
            <button
              className="btn ghost small button-ghost"
              data-testid="rn-close-editor"
              onClick={() => setDraft(null)}
            >
              Close
            </button>
          </div>

          <div className="rn-form-grid">
            <label className="rn-field">
              <span className="rn-label">Version</span>
              <input
                type="text"
                className="rn-input"
                data-testid="rn-edit-version"
                value={draft.version}
                onChange={(e) => setDraftField("version", e.target.value)}
              />
            </label>
            <label className="rn-field">
              <span className="rn-label">Title</span>
              <input
                type="text"
                className="rn-input"
                data-testid="rn-edit-title"
                value={draft.title}
                onChange={(e) => setDraftField("title", e.target.value)}
              />
            </label>
            <label className="rn-field">
              <span className="rn-label">Subtitle</span>
              <input
                type="text"
                className="rn-input"
                data-testid="rn-edit-subtitle"
                value={draft.subtitle}
                onChange={(e) => setDraftField("subtitle", e.target.value)}
              />
            </label>
          </div>

          <div className="rn-highlights">
            <div className="rn-highlights-head">
              <span className="section-label">Highlights</span>
              <button
                className="btn small button-ghost"
                data-testid="rn-add-highlight"
                onClick={addHighlight}
              >
                + Add highlight
              </button>
            </div>

            {draft.highlights.length === 0 && (
              <div className="rn-muted">No highlights yet.</div>
            )}

            {draft.highlights.map((h, i) => (
              <div className="rn-highlight" data-testid="rn-highlight" key={i}>
                {!isRichHighlight(h) ? (
                  <div className="rn-highlight-string">
                    <span className="rn-highlight-plain" data-testid="rn-highlight-plain">
                      {String(h)}
                    </span>
                    <div className="rn-highlight-string-actions">
                      <button
                        className="btn small primary button-primary"
                        data-testid="rn-promote-btn"
                        onClick={() => setHighlight(i, promoteHighlight(h))}
                      >
                        Promote to rich
                      </button>
                      <button
                        className="btn small ghost button-ghost"
                        onClick={() => removeHighlight(i)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rn-highlight-rich" data-testid="rn-highlight-rich">
                    <div className="rn-highlight-grid">
                      <label className="rn-field rn-field--icon">
                        <span className="rn-label">Icon</span>
                        <input
                          type="text"
                          className="rn-input"
                          value={h.icon || ""}
                          onChange={(e) => setHighlightField(i, "icon", e.target.value)}
                        />
                      </label>
                      <label className="rn-field">
                        <span className="rn-label">Title</span>
                        <input
                          type="text"
                          className="rn-input"
                          value={h.title || ""}
                          onChange={(e) => setHighlightField(i, "title", e.target.value)}
                        />
                      </label>
                      <label className="rn-field rn-field--badge">
                        <span className="rn-label">Badge (optional)</span>
                        <input
                          type="text"
                          className="rn-input"
                          value={h.badge || ""}
                          onChange={(e) => setHighlightField(i, "badge", e.target.value)}
                        />
                      </label>
                    </div>
                    <label className="rn-field rn-field--full">
                      <span className="rn-label">Body</span>
                      <textarea
                        className="rn-textarea"
                        rows={2}
                        value={h.body || ""}
                        onChange={(e) => setHighlightField(i, "body", e.target.value)}
                      />
                    </label>
                    <label className="rn-field rn-field--full">
                      <span className="rn-label">Try it (optional)</span>
                      <input
                        type="text"
                        className="rn-input"
                        value={h.tryIt || ""}
                        onChange={(e) => setHighlightField(i, "tryIt", e.target.value)}
                      />
                    </label>
                    <div className="rn-highlight-rich-actions">
                      <button
                        className="btn small ghost button-ghost"
                        onClick={() => removeHighlight(i)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="rn-actions">
            <button
              className="btn primary button-primary"
              data-testid="rn-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              className="btn button-ghost"
              data-testid="rn-editor-publish-btn"
              onClick={() => handlePublish(draft)}
              disabled={saving}
            >
              Publish
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default inject("userStore")(observer(ReleaseNotes));
